import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DatePreset, DATE_PRESETS } from '../../types/filters';
import { getDatePresetOptions, formatDateForInput, getDateRangeForPreset } from '../../utils/dateUtils';

interface DatePresetSelectorProps {
  onDateRangeChange: (from: string, to: string, preset: DatePreset) => void;
  fromDate: string;
  toDate: string;
  className?: string;
}

export function DatePresetSelector({
  onDateRangeChange,
  fromDate,
  toDate,
  className
}: DatePresetSelectorProps) {
  const [selectedPreset, setSelectedPreset] = useState<DatePreset>(DATE_PRESETS.ALL_DATES);
  const presetOptions = getDatePresetOptions();
  
  // Determine if current date range matches a preset
  useEffect(() => {
    if (!fromDate && !toDate) {
      setSelectedPreset(DATE_PRESETS.ALL_DATES);
      return;
    }
    
    // Check if current date range matches any preset
    for (const preset of Object.values(DATE_PRESETS)) {
      if (preset === DATE_PRESETS.DATE_RANGE) continue;
      
      const range = getDateRangeForPreset(preset);
      const presetFromDate = formatDateForInput(range.from);
      const presetToDate = formatDateForInput(range.to);
      
      if (presetFromDate === fromDate && presetToDate === toDate) {
        setSelectedPreset(preset);
        return;
      }
    }
    
    // If no match found, it's a date range
    setSelectedPreset(DATE_PRESETS.DATE_RANGE);
  }, [fromDate, toDate]);
  
  // Handle preset selection
  const handlePresetChange = (value: string) => {
    const preset = value as DatePreset;
    setSelectedPreset(preset);
    
    // Apply the preset date range
    const range = getDateRangeForPreset(preset);
    const from = formatDateForInput(range.from);
    const to = formatDateForInput(range.to);
    onDateRangeChange(from, to, preset);
  };
  
  return (
    <div className={className}>
      <h3 className="text-sm font-medium mb-2 dark:text-white">Date Range</h3>
      <Select
        value={selectedPreset}
        onValueChange={handlePresetChange}
      >
        <SelectTrigger className="w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white">
          <SelectValue placeholder="Select date range" />
        </SelectTrigger>
        <SelectContent>
          {presetOptions.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
